/**
 * Admin hooks (P30 / B-path) — workgroups, oauth clients, virtual servers,
 * approvals, credentials, policies. Read hooks plus approval mutations for
 * Round 1.3 drilldown drawer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, ApiError } from './client.js';

export interface WorkgroupRow {
  id: string;
  display_name: string;
  description: string | null;
  owner_sunet: string;
  source: string;
  is_reviewer_wg: boolean;
}

export interface OauthClientRow {
  id: string;
  type: string;
  displayName: string;
  ownerWgId: string;
  allowedScopes: string[];
  redirectUris: string[];
  allowedVirtualServerSlugs: string[];
  status: string;
  lastActiveAt: string | null;
}

export interface VirtualServerRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  endpointUrl: string;
  allowedClientIds: string[];
  composition: unknown;
  rateLimitOverrides: unknown;
  status: string;
  /**
   * E36 — underlying mcp_server.backend_type. Used by the Connector
   * Catalog UI to filter Private (native_mcp / rest_wrapper) vs
   * Composio-federated backends. `null` when the join couldn't
   * resolve a paired mcp_server (shouldn't happen for E35-auto-
   * materialised rows).
   */
  backend_type?: string | null;
}

export interface ApprovalRow {
  id: string;
  assetType: string;
  assetId: string;
  requesterSub: string;
  requesterWgSnapshot: string[];
  riskTier: string;
  riskNotes: string | null;
  requiredSteps: {
    step: string;
    reviewer_wg_id: string;
    decided: boolean;
    decision?: 'approved' | 'rejected' | 'changes_requested';
    decider_sub?: string;
    decided_at?: string;
    notes?: string;
  }[];
  state: 'pending' | 'in_review' | 'approved' | 'rejected' | 'withdrawn';
  slaDueAt: string | null;
  createdAt: string;
  updatedAt: string;
  decidedAt: string | null;
}

export interface CredentialBindingRow {
  id: string;
  serverId: string;
  mode: string;
  secretRef: string;
  tokenExchangeProfile: unknown;
  allowedUse: unknown;
  rotationPolicy: unknown;
}

export interface PolicyRuleRow {
  id: string;
  priority: number;
  name: string;
  subjectWgAnyOf: string[] | null;
  subjectWgAllOf: string[] | null;
  subjectWgNoneOf: string[] | null;
  clientSelector: string[] | null;
  resourceSelector: unknown;
  action: string;
  effect: string;
  conditions: unknown;
  reasonCode: string;
  version: string;
  status: string;
}

interface ListResponse<T> {
  items: T[];
}

export interface WorkgroupListFilter {
  q?: string;
  reviewer?: 'true' | 'false';
  limit?: number;
  offset?: number;
}

export interface WorkgroupListResponse {
  items: WorkgroupRow[];
  total: number;
  limit: number;
  offset: number;
}

export function useWorkgroups(filter: WorkgroupListFilter = {}) {
  const qs = new URLSearchParams();
  if (filter.q) qs.set('q', filter.q);
  if (filter.reviewer) qs.set('reviewer', filter.reviewer);
  if (filter.limit) qs.set('limit', String(filter.limit));
  if (filter.offset) qs.set('offset', String(filter.offset));
  const search = qs.toString();
  return useQuery({
    queryKey: ['workgroups', filter],
    queryFn: () =>
      apiFetch<WorkgroupListResponse>(`/api/v1/workgroups${search ? `?${search}` : ''}`),
    placeholderData: (prev) => prev,
  });
}

export function useWorkgroup(id: string | undefined) {
  return useQuery({
    queryKey: ['workgroups', 'one', id],
    queryFn: () => apiFetch<WorkgroupRow>(`/api/v1/workgroups/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

export interface CreateWorkgroupInput {
  id: string;
  display_name: string;
  description?: string;
  owner_sunet?: string;
  is_reviewer_wg?: boolean;
}

export function useCreateWorkgroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkgroupInput) =>
      apiFetch<WorkgroupRow>('/api/v1/workgroups', {
        method: 'POST',
        body: { ...input, source: 'manual' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workgroups'] }),
  });
}

export interface UpdateWorkgroupInput {
  display_name?: string;
  description?: string;
  owner_sunet?: string;
  is_reviewer_wg?: boolean;
}

export function useUpdateWorkgroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateWorkgroupInput }) =>
      apiFetch<WorkgroupRow>(`/api/v1/workgroups/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workgroups'] }),
  });
}

export function useDeleteWorkgroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/workgroups/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workgroups'] });
      qc.invalidateQueries({ queryKey: ['servers'] });
    },
  });
}

export interface WgMemberRow {
  wg_id: string;
  principal_sub: string;
  added_at: string;
  added_by_sub?: string;
  expires_at?: string | null;
  source?: string;
}

export function useWorkgroupMembers(wgId: string | undefined) {
  return useQuery({
    queryKey: ['workgroup-members', wgId],
    queryFn: () =>
      apiFetch<ListResponse<WgMemberRow>>(
        `/api/v1/workgroups/${encodeURIComponent(wgId!)}/members`,
      ),
    enabled: Boolean(wgId),
  });
}

export function useAddWgMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ wgId, principalSub }: { wgId: string; principalSub: string }) =>
      apiFetch<WgMemberRow>(`/api/v1/workgroups/${encodeURIComponent(wgId)}/members`, {
        method: 'POST',
        body: { principal_sub: principalSub },
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['workgroup-members', vars.wgId] }),
  });
}

export function useRemoveWgMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ wgId, principalSub }: { wgId: string; principalSub: string }) =>
      apiFetch<void>(
        `/api/v1/workgroups/${encodeURIComponent(wgId)}/members/${encodeURIComponent(principalSub)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['workgroup-members', vars.wgId] }),
  });
}

export interface BulkImportRow {
  wg_id: string;
  principal_sub: string;
  expires_at?: string;
}

export interface BulkImportResult {
  imported: number;
  errors: { row: unknown; reason: string }[];
}

/**
 * Parse a CSV string in the format `wg_id,principal_sub[,expires_at]` —
 * one row per non-blank line, blank lines and `# ...` comments ignored.
 * Used by the WorkgroupDetail bulk-import button before hitting
 * `useImportWgMembersBulk` below.
 */
export function parseMembershipCsv(csv: string, fixedWgId?: string): BulkImportRow[] {
  const rows: BulkImportRow[] = [];
  for (const raw of csv.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const cols = line.split(',').map((s) => s.trim());
    let wg: string;
    let sub: string;
    let exp: string | undefined;
    if (fixedWgId) {
      // Single-WG context: only sub[,expires_at]
      wg = fixedWgId;
      sub = cols[0]!;
      exp = cols[1] || undefined;
    } else {
      wg = cols[0]!;
      sub = cols[1]!;
      exp = cols[2] || undefined;
    }
    if (!wg || !sub) continue;
    rows.push({ wg_id: wg, principal_sub: sub, ...(exp ? { expires_at: exp } : {}) });
  }
  return rows;
}

/**
 * Reverse lookup — what WGs is a principal in? Used by Phase 2.A.5
 * "view as user" helper.
 */
export function usePrincipalWorkgroups(sub: string | undefined) {
  return useQuery({
    queryKey: ['principals', 'wgs', sub],
    queryFn: () =>
      apiFetch<ListResponse<WorkgroupRow>>(
        `/api/v1/principals/${encodeURIComponent(sub!)}/workgroups`,
      ),
    enabled: Boolean(sub && sub.trim().length > 0),
  });
}

export function useImportWgMembersBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rows }: { rows: BulkImportRow[] }) =>
      apiFetch<BulkImportResult>('/api/v1/workgroups/import', {
        method: 'POST',
        body: { rows },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workgroup-members'] });
      qc.invalidateQueries({ queryKey: ['workgroups'] });
    },
  });
}

export interface OauthClientListFilter {
  q?: string;
  type?: string;
  ownerWg?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface OauthClientListResponse {
  items: OauthClientRow[];
  total: number;
  limit: number;
  offset: number;
}

export function useClients(filter: OauthClientListFilter = {}) {
  const qs = new URLSearchParams();
  if (filter.q) qs.set('q', filter.q);
  if (filter.type) qs.set('type', filter.type);
  if (filter.ownerWg) qs.set('owner_wg', filter.ownerWg);
  if (filter.status) qs.set('status', filter.status);
  if (filter.limit) qs.set('limit', String(filter.limit));
  if (filter.offset) qs.set('offset', String(filter.offset));
  const search = qs.toString();
  return useQuery({
    queryKey: ['clients', filter],
    queryFn: () =>
      apiFetch<OauthClientListResponse>(`/api/v1/clients${search ? `?${search}` : ''}`),
    placeholderData: (prev) => prev,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'one', id],
    queryFn: () => apiFetch<OauthClientRow>(`/api/v1/clients/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

export interface CreateClientInput {
  id?: string;
  type: string;
  display_name: string;
  owner_wg_id: string;
  allowed_scopes?: string[];
  redirect_uris?: string[];
  allowed_virtual_server_slugs?: string[];
  status?: string;
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) =>
      apiFetch<OauthClientRow>('/api/v1/clients', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export interface UpdateClientInput {
  display_name?: string;
  allowed_scopes?: string[];
  redirect_uris?: string[];
  allowed_virtual_server_slugs?: string[];
  status?: string;
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateClientInput }) =>
      apiFetch<OauthClientRow>(`/api/v1/clients/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/clients/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Backend health probe (Group 8)
// ─────────────────────────────────────────────────────────────────────────

export interface ProbeToolsResult {
  ok: boolean;
  added: number;
  updated: number;
  retired: number;
  error?: string;
}

export function useProbeServerTools() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serverId: string) =>
      apiFetch<ProbeToolsResult>(`/api/v1/servers/${encodeURIComponent(serverId)}/probe-tools`, {
        method: 'POST',
      }),
    onSuccess: (_d, serverId) => {
      qc.invalidateQueries({ queryKey: ['tools', serverId] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

export interface ServerHealthSnapshot {
  serverId: string;
  status: 'up' | 'down' | 'unknown';
  httpStatus?: number;
  latencyMs?: number;
  checkedAt: string;
  error?: string;
}

export function useServerHealthAll() {
  return useQuery({
    queryKey: ['servers-health'],
    queryFn: () => apiFetch<{ items: ServerHealthSnapshot[] }>('/api/v1/servers/health'),
    refetchInterval: 30_000,
  });
}

export function useServerHealth(id: string | undefined) {
  return useQuery({
    queryKey: ['servers-health', 'one', id],
    queryFn: () =>
      apiFetch<ServerHealthSnapshot>(`/api/v1/servers/${encodeURIComponent(id!)}/health`),
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });
}

export interface ClientTestResult {
  ok: boolean;
  client_id?: string;
  type?: string;
  status?: string;
  owner_wg_id?: string;
  allowed_scopes?: string[];
  redirect_uris?: string[];
  issues?: string[];
  next_steps?: string[];
  error?: string;
}

export function useTestClient() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ClientTestResult>(`/api/v1/clients/${encodeURIComponent(id)}/test`, {
        method: 'POST',
      }),
  });
}

export interface VirtualServerListFilter {
  q?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface VirtualServerListResponse {
  items: VirtualServerRow[];
  total: number;
  limit: number;
  offset: number;
}

export function useVirtualServersPaged(filter: VirtualServerListFilter = {}) {
  const qs = new URLSearchParams();
  if (filter.q) qs.set('q', filter.q);
  if (filter.status) qs.set('status', filter.status);
  if (filter.limit) qs.set('limit', String(filter.limit));
  if (filter.offset) qs.set('offset', String(filter.offset));
  const search = qs.toString();
  return useQuery({
    queryKey: ['virtual-servers-paged', filter],
    queryFn: () =>
      apiFetch<VirtualServerListResponse>(`/api/v1/virtual-servers${search ? `?${search}` : ''}`),
    placeholderData: (prev) => prev,
  });
}

export function useVirtualServer(id: string | undefined) {
  return useQuery({
    queryKey: ['virtual-servers', 'one', id],
    queryFn: () => apiFetch<VirtualServerRow>(`/api/v1/virtual-servers/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

export interface CompositionEntry {
  tool_id: string;
  exposure: 'included' | 'requestable' | 'excluded';
  policy?: 'inherited' | 'explicit';
}

export interface CreateVirtualServerInput {
  slug: string;
  name: string;
  description?: string;
  endpoint_url: string;
  allowed_client_ids?: string[];
  composition?: CompositionEntry[];
  rate_limit_overrides?: { per_user_rps?: number; per_tool_rps?: number };
  status?: string;
}

export function useCreateVirtualServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVirtualServerInput) =>
      apiFetch<VirtualServerRow>('/api/v1/virtual-servers', { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['virtual-servers'] });
      qc.invalidateQueries({ queryKey: ['virtual-servers-paged'] });
    },
  });
}

export interface UpdateVirtualServerInput {
  name?: string;
  description?: string;
  endpoint_url?: string;
  allowed_client_ids?: string[];
  composition?: CompositionEntry[];
  rate_limit_overrides?: { per_user_rps?: number; per_tool_rps?: number };
  status?: string;
}

export function useUpdateVirtualServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateVirtualServerInput }) =>
      apiFetch<VirtualServerRow>(`/api/v1/virtual-servers/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['virtual-servers'] });
      qc.invalidateQueries({ queryKey: ['virtual-servers-paged'] });
    },
  });
}

export function useDeleteVirtualServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/virtual-servers/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['virtual-servers'] });
      qc.invalidateQueries({ queryKey: ['virtual-servers-paged'] });
    },
  });
}

export function useVirtualServers() {
  return useQuery({
    queryKey: ['virtual-servers'],
    queryFn: () => apiFetch<ListResponse<VirtualServerRow>>('/api/v1/virtual-servers'),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// E36 — Composio-backed connector status + initiate
// ─────────────────────────────────────────────────────────────────────────

export interface ConnectorStatus {
  slug: string;
  connected: boolean;
  status: string;
  connection_id?: string | null;
}

/**
 * Look up the caller's connection status for a Composio-backed connector.
 * Returns `null` when the slug isn't a Composio provider (404 from the
 * gateway). The Catalog uses this to decide whether to show "Connect"
 * or "Add to Claude" on each card.
 */
export function useConnectorStatus(slug: string | undefined) {
  return useQuery({
    queryKey: ['connector-status', slug],
    enabled: Boolean(slug),
    queryFn: async (): Promise<ConnectorStatus | null> => {
      try {
        return await apiFetch<ConnectorStatus>(
          `/api/v1/connectors/${encodeURIComponent(slug!)}/status`,
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    // Re-poll once the user comes back from the OAuth popup so the
    // card flips to "Connected" without a manual reload.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/** Initiate an OAuth flow for a Composio-backed connector. */
export function useInitiateConnector() {
  return useMutation({
    mutationFn: ({ slug }: { slug: string }) =>
      apiFetch<{ redirect_url: string; connection_id: string | null }>(
        `/api/v1/connectors/${encodeURIComponent(slug)}/connect`,
        { method: 'POST' },
      ),
  });
}

/** Disconnect (revoke) a Composio-backed connector. */
export function useDisconnectConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }: { slug: string }) =>
      apiFetch<{ slug: string; found: number; deleted: number }>(
        `/api/v1/connectors/${encodeURIComponent(slug)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, { slug }) => {
      qc.invalidateQueries({ queryKey: ['connector-status', slug] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Wrapper specs (Phase 2.A.4 — REST-wrapper auto-gen)
// ─────────────────────────────────────────────────────────────────────────

export interface WrapperSpecRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  owningWgId: string;
  spec: unknown;
  status: string;
  materializedServerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WrapperSpecListFilter {
  q?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface WrapperSpecListResponse {
  items: WrapperSpecRow[];
  total: number;
  limit: number;
  offset: number;
}

export function useWrapperSpecs(filter: WrapperSpecListFilter = {}) {
  const qs = new URLSearchParams();
  if (filter.q) qs.set('q', filter.q);
  if (filter.status) qs.set('status', filter.status);
  if (filter.limit) qs.set('limit', String(filter.limit));
  if (filter.offset) qs.set('offset', String(filter.offset));
  const search = qs.toString();
  return useQuery({
    queryKey: ['wrapper-specs', filter],
    queryFn: () =>
      apiFetch<WrapperSpecListResponse>(`/api/v1/wrapper-specs${search ? `?${search}` : ''}`),
    placeholderData: (prev) => prev,
  });
}

export function useCreateWrapperSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spec, activate }: { spec: unknown; activate?: boolean }) =>
      apiFetch<{ spec: WrapperSpecRow; tool_count: number }>('/api/v1/wrapper-specs', {
        method: 'POST',
        body: { spec, activate },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wrapper-specs'] });
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

export function useActivateWrapperSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<WrapperSpecRow>(`/api/v1/wrapper-specs/${encodeURIComponent(id)}/activate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wrapper-specs'] });
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

export function useDeleteWrapperSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/wrapper-specs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wrapper-specs'] });
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

export function useWrapperSpec(id: string | undefined) {
  return useQuery({
    queryKey: ['wrapper-specs', 'one', id],
    queryFn: () => apiFetch<WrapperSpecRow>(`/api/v1/wrapper-specs/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

/** Look up a wrapper spec by slug (uses the list endpoint with q filter). */
export function useWrapperSpecBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['wrapper-specs', 'by-slug', slug],
    queryFn: async () => {
      const r = await apiFetch<WrapperSpecListResponse>(
        `/api/v1/wrapper-specs?q=${encodeURIComponent(slug!)}&limit=10`,
      );
      return r.items.find((s) => s.slug === slug) ?? null;
    },
    enabled: Boolean(slug),
  });
}

export function useUpdateWrapperSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, spec, status }: { id: string; spec?: unknown; status?: string }) =>
      apiFetch<WrapperSpecRow>(`/api/v1/wrapper-specs/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: { spec, status },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wrapper-specs'] });
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Wrapper credentials (UI-managed, encrypted at rest)
// ─────────────────────────────────────────────────────────────────────────

export type WrapperCredentialMode =
  | 'none'
  | 'bearer'
  | 'api_key_header'
  | 'api_key_query'
  | 'basic'
  | 'oauth_client_credentials'
  | 'custom_headers';

export interface WrapperCredentialView {
  slug: string;
  realm_name: string;
  mode: WrapperCredentialMode;
  fields: Record<string, unknown>;
  created_at: string;
  created_by: string;
  rotated_at: string;
  last_used_at: string | null;
}

export function useWrapperCredentials(slug: string | undefined) {
  return useQuery({
    queryKey: ['wrapper-credentials', slug],
    queryFn: () =>
      apiFetch<{ items: WrapperCredentialView[] }>(
        `/api/v1/wrapper-credentials/${encodeURIComponent(slug!)}`,
      ),
    enabled: Boolean(slug),
  });
}

export interface UpsertWrapperCredentialInput {
  slug: string;
  realm_name: string;
  mode: WrapperCredentialMode;
  fields: Record<string, unknown>;
}

export function useUpsertWrapperCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, realm_name, mode, fields }: UpsertWrapperCredentialInput) =>
      apiFetch<WrapperCredentialView>(
        `/api/v1/wrapper-credentials/${encodeURIComponent(slug)}/${encodeURIComponent(realm_name)}`,
        { method: 'PUT', body: { mode, fields } },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['wrapper-credentials', vars.slug] });
    },
  });
}

export function useDeleteWrapperCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, realm_name }: { slug: string; realm_name: string }) =>
      apiFetch<void>(
        `/api/v1/wrapper-credentials/${encodeURIComponent(slug)}/${encodeURIComponent(realm_name)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['wrapper-credentials', vars.slug] }),
  });
}

export interface TestAuthResult {
  ok: boolean;
  status?: number;
  error_body?: string;
  error?: string;
  message?: string;
  expires_in?: number;
  mode?: WrapperCredentialMode;
}

export function useTestWrapperAuth() {
  return useMutation({
    mutationFn: ({ slug, realm_name }: { slug: string; realm_name: string }) =>
      apiFetch<TestAuthResult>(
        `/api/v1/wrapper-credentials/${encodeURIComponent(slug)}/${encodeURIComponent(realm_name)}/test-auth`,
        { method: 'POST' },
      ),
  });
}

export interface TestToolCallResult {
  ok: boolean;
  status?: number;
  latency_ms?: number;
  body?: string;
  error?: string;
}

export function useTestWrapperTool() {
  return useMutation({
    mutationFn: ({
      slug,
      tool_name,
      args,
    }: {
      slug: string;
      tool_name: string;
      args: Record<string, unknown>;
    }) =>
      apiFetch<TestToolCallResult>(
        `/api/v1/wrapper-credentials/${encodeURIComponent(slug)}/test-tool`,
        { method: 'POST', body: { tool_name, arguments: args } },
      ),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Validation (Phase 2.A.4 follow-up — wizard preflight)
// ─────────────────────────────────────────────────────────────────────────

export interface CheckResult {
  name: string;
  severity: 'error' | 'warn' | 'info';
  ok: boolean;
  message: string;
  detail?: unknown;
}

export interface ValidationResponse {
  checks: CheckResult[];
  has_error?: boolean;
  error?: string;
}

export interface ValidateServerInput {
  mode: 'endpoint' | 'hosted';
  slug: string;
  owning_wg_id: string;
  backend_url?: string;
  port?: number;
}

export function useValidateServer() {
  return useMutation({
    mutationFn: (input: ValidateServerInput) =>
      apiFetch<ValidationResponse>('/api/v1/servers/validate', {
        method: 'POST',
        body: input,
      }),
  });
}

export function useValidateWrapperSpec() {
  return useMutation({
    mutationFn: ({ spec, currentSlug }: { spec: unknown; currentSlug?: string }) =>
      apiFetch<ValidationResponse>('/api/v1/wrapper-specs/validate', {
        method: 'POST',
        body: { spec, current_slug: currentSlug },
      }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Server registration (Phase 2.A.4 — wizard)
// ─────────────────────────────────────────────────────────────────────────

export interface CreateServerInput {
  slug: string;
  name: string;
  description?: string;
  owning_wg_id: string;
  support_wg_id?: string;
  cost_attribution_wg_id?: string;
  backend_type: 'native_mcp' | 'rest_wrapper';
  backend_url: string;
  environment?: string;
  risk_tier: 'low' | 'medium' | 'high' | 'critical';
}

export function useCreateServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServerInput) =>
      apiFetch<{ id: string; slug: string }>('/api/v1/servers', { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Tool-level edit
// ─────────────────────────────────────────────────────────────────────────

export interface UpdateToolInput {
  display_name?: string;
  description?: string;
  data_classification?: string;
  side_effect_level?: string;
  status?: string;
  restricted_to_wg_ids?: string[];
  /**
   * When true, any authenticated principal with at least one Stanford
   * WG can call this tool. When false (default), the principal must be
   * in at least one WG in `restricted_to_wg_ids` — and an empty list
   * means nobody can call it (closed by default per migration 0007).
   */
  open_to_all_wgs?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Pre-registration: probe a backend URL and classify the result
// ─────────────────────────────────────────────────────────────────────────

export interface TestConnectionResult {
  ok: boolean;
  /**
   * Categorized failure: see `ProbeFailureKind` in the control-plane.
   * Undefined when ok=true. Examples: 'unauthorized', 'stateful_session',
   * 'unreachable', 'tls_error', 'method_not_found', 'rpc_error'.
   */
  kind?: string;
  summary: string;
  remediation?: string;
  rawError?: string;
  status?: number;
  authenticated?: boolean;
  toolCount?: number;
  tools?: ReadonlyArray<{
    name: string;
    title?: string;
    description?: string;
  }>;
}

export function useTestConnection() {
  return useMutation({
    mutationFn: ({ backend_url, backend_type }: { backend_url: string; backend_type?: string }) =>
      apiFetch<TestConnectionResult>('/api/v1/servers/test-connection', {
        method: 'POST',
        body: { backend_url, backend_type },
      }),
  });
}

export function useUpdateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateToolInput }) =>
      apiFetch<unknown>(`/api/v1/tools/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tools'] });
      qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}

export interface ApprovalFilter {
  state?: string;
  reviewable?: 'true' | 'false';
  mine?: 'true' | 'false';
}

export function useApprovals(filter: ApprovalFilter = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) if (v) qs.set(k, String(v));
  const search = qs.toString();
  return useQuery({
    queryKey: ['approvals', filter],
    queryFn: () =>
      apiFetch<ListResponse<ApprovalRow>>(`/api/v1/approvals${search ? `?${search}` : ''}`),
  });
}

export function useCredentials(serverId?: string) {
  const url = serverId ? `/api/v1/credentials?server=${serverId}` : '/api/v1/credentials';
  return useQuery({
    queryKey: ['credentials', serverId ?? 'all'],
    queryFn: () => apiFetch<ListResponse<CredentialBindingRow>>(url),
  });
}

export function usePolicies() {
  return useQuery({
    queryKey: ['policies'],
    queryFn: () => apiFetch<ListResponse<PolicyRuleRow>>('/api/v1/policies'),
  });
}

/**
 * Policy-rule CRUD (E01 — Policy Studio create / edit / retire).
 *
 * Payload shape matches the control-plane's `/api/v1/policies` handler
 * (snake_case body, returns the persisted row in `PolicyRuleRow` shape).
 * The proxy's `PolicyRulesetLoader` hot-reloads every 30s, so any
 * change made through these hooks is live without a restart.
 */
export interface CreatePolicyInput {
  priority: number;
  name: string;
  action: string;
  effect: 'allow' | 'deny';
  reason_code: string;
  subject_wg_any_of?: string[];
  subject_wg_all_of?: string[];
  subject_wg_none_of?: string[];
  client_selector?: string[];
  resource_selector?: Record<string, unknown>;
  conditions?: unknown[];
  version?: string;
  status?: 'draft' | 'active' | 'deprecated';
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePolicyInput) =>
      apiFetch<PolicyRuleRow>('/api/v1/policies', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export type UpdatePolicyInput = Partial<CreatePolicyInput>;

export function useUpdatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdatePolicyInput }) =>
      apiFetch<PolicyRuleRow>(`/api/v1/policies/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/policies/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Approval mutations (P23 routes; surfaced for Round 1.3 drilldown)
// ─────────────────────────────────────────────────────────────────────────

export interface ApprovalDecideInput {
  approvalId: string;
  decision: 'approved' | 'rejected' | 'changes_requested';
  notes?: string;
}

export function useApprovalDecide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ approvalId, decision, notes }: ApprovalDecideInput) =>
      apiFetch<ApprovalRow>(`/api/v1/approvals/${encodeURIComponent(approvalId)}/decision`, {
        method: 'POST',
        body: { decision, notes },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export function useApprovalWithdraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) =>
      apiFetch<ApprovalRow>(`/api/v1/approvals/${encodeURIComponent(approvalId)}/withdraw`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export function useApprovalEscalate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) =>
      apiFetch<ApprovalRow>(`/api/v1/approvals/${encodeURIComponent(approvalId)}/escalate`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export interface ApprovalSubmitInput {
  asset_type: 'access_request' | 'server' | 'owner_transfer' | 'tool' | 'policy_exception';
  asset_id: string;
  risk_tier?: 'low' | 'medium' | 'high' | 'critical';
  risk_notes?: string;
  sla_hours?: number;
}

export function useApprovalSubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApprovalSubmitInput) =>
      apiFetch<ApprovalRow>('/api/v1/approvals', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

// ──────────────────────────────────────────────────────────────────────
// E39 — Composio Tool Router toolkit allowlist
// ──────────────────────────────────────────────────────────────────────

/** A toolkit Stanford has made a decision about. */
export interface ComposioToolkitRow {
  toolkit_slug: string;
  display_name: string;
  status: 'approved' | 'blocked';
  allowed_wg_ids: string[];
  tool_allow: string[] | null;
  tool_deny: string[] | null;
  required_tags: string[] | null;
  auth_config_id: string | null;
  approved_by: string | null;
  approved_at: string | null;
  notes: string | null;
}

/**
 * One of Composio's ~1,089 toolkits, annotated with our decision.
 * `status: 'unapproved'` means nobody has looked at it — the default,
 * since the allowlist is closed.
 */
export interface ComposioAvailableRow {
  toolkit_slug: string;
  display_name: string;
  status: 'approved' | 'blocked' | 'unapproved';
  auth_schemes: string[];
  /**
   * Every auth mode this toolkit supports hands all Stanford callers the
   * SAME identity at the provider. Approving one removes per-user data
   * separation entirely — the ServiceNow finding of 2026-06-02.
   */
  shared_identity_only: boolean;
}

export interface ApproveToolkitInput {
  slug: string;
  display_name?: string;
  allowed_wg_ids?: string[];
  tool_allow?: string[] | null;
  tool_deny?: string[] | null;
  required_tags?: string[] | null;
  notes?: string | null;
  /** Required by the API to approve a shared-identity toolkit. */
  acknowledge_shared_identity?: boolean;
}

export function useComposioToolkits() {
  return useQuery({
    queryKey: ['composio-toolkits'],
    queryFn: () => apiFetch<ListResponse<ComposioToolkitRow>>('/api/v1/admin/composio/toolkits'),
  });
}

/**
 * Composio's full catalog. Deliberately a separate query from the
 * allowlist: it costs ~11 upstream page fetches, so it is only pulled
 * when the operator opens the "add a toolkit" view, not on every render
 * of the allowlist itself.
 */
export function useComposioAvailable(enabled: boolean) {
  return useQuery({
    queryKey: ['composio-available'],
    queryFn: () => apiFetch<ListResponse<ComposioAvailableRow>>('/api/v1/admin/composio/available'),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useApproveComposioToolkit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, ...body }: ApproveToolkitInput) =>
      apiFetch<{ toolkit_slug: string; status: string }>(
        `/api/v1/admin/composio/toolkits/${encodeURIComponent(slug)}/approve`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['composio-toolkits'] });
      void qc.invalidateQueries({ queryKey: ['composio-available'] });
    },
  });
}

export function useBlockComposioToolkit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, notes }: { slug: string; notes?: string }) =>
      apiFetch<{ toolkit_slug: string; status: string }>(
        `/api/v1/admin/composio/toolkits/${encodeURIComponent(slug)}/block`,
        { method: 'POST', body: { notes } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['composio-toolkits'] });
      void qc.invalidateQueries({ queryKey: ['composio-available'] });
    },
  });
}
